import React, { useState, useEffect } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Sun, Moon, ArrowRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useTheme } from '../ThemeProvider';

const navItems = [
  { name: 'About', to: 'about', type: 'scroll' },
  { name: 'Products', to: 'products', type: 'scroll' },
  { name: 'Solutions', to: 'solutions', type: 'scroll' },
  { name: 'Innovation', to: 'innovation', type: 'scroll' },
  { name: 'Careers', to: '/hiring', type: 'route' },
  { name: 'Contact', to: 'contact', type: 'scroll' },
];

const Navbar = ({ showApply = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogoClick = () => {
    if (!isHome) {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNavClick = (item) => {
    if (item.type === 'route') {
      navigate(item.to);
    } else {
      if (!isHome) {
        navigate('/', { state: { scrollTo: item.to } });
      }
    }
    setIsOpen(false);
  };

  const handleGetInTouch = () => {
    if (isHome) {
      const contactEl = document.getElementById('contact');
      if (contactEl) {
        contactEl.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/', { state: { scrollTo: 'contact' } });
    }
    setIsOpen(false);
  };

  return (
    <header
      className={cn(
        'fixed top-0 left-0 w-full z-50 transition-all duration-200',
        scrolled
          ? 'bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200/90 dark:border-slate-800 shadow-subtle py-3'
          : 'bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-b border-slate-200/50 dark:border-slate-800/50 py-4'
      )}
    >
      <div className="container mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Brand Logo & Name */}
        <div 
          onClick={handleLogoClick}
          className="flex items-center gap-3 cursor-pointer group select-none"
        >
          <img 
            src="/logo192.jpeg" 
            alt="Mira Future Tech Vision Logo" 
            className="w-10 h-10 rounded-xl object-contain border border-slate-200 dark:border-slate-700 bg-white shadow-xs group-hover:scale-105 transition-transform duration-200"
          />
          <div className="flex flex-col">
            <span className="font-display font-bold text-sm md:text-base tracking-tight text-slate-900 dark:text-white leading-tight">
              MIRA FUTURE TECH VISION
            </span>
            <span className="text-[10px] tracking-widest uppercase font-semibold text-slate-500 dark:text-slate-400">
              PVT. LTD.
            </span>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-1 xl:space-x-2">
          {navItems.map((item) => {
            if (item.type === 'scroll' && isHome) {
              return (
                <ScrollLink
                  key={item.name}
                  to={item.to}
                  smooth={true}
                  duration={400}
                  spy={true}
                  offset={-75}
                  activeClass="text-brand-600 dark:text-brand-400 font-semibold bg-slate-50 dark:bg-slate-800/60"
                  className="px-3.5 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors cursor-pointer"
                >
                  {item.name}
                </ScrollLink>
              );
            }

            const isActiveRoute = item.type === 'route' && location.pathname === item.to;
            return (
              <button
                key={item.name}
                onClick={() => handleNavClick(item)}
                className={cn(
                  'px-3.5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer',
                  isActiveRoute
                    ? 'text-brand-600 dark:text-brand-400 font-semibold bg-slate-50 dark:bg-slate-800/60'
                    : 'text-slate-700 dark:text-slate-200 hover:text-brand-600 dark:hover:text-brand-400 hover:bg-slate-50 dark:hover:bg-slate-800/40'
                )}
              >
                {item.name}
              </button>
            );
          })}
        </nav>

        {/* Action Button & Theme Toggle */}
        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-xl text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Toggle visual theme"
            title="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <button
            onClick={handleGetInTouch}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold shadow-xs hover:shadow transition-all duration-200 active:scale-98"
          >
            Get in Touch
            <ArrowRight size={15} />
          </button>
        </div>

        {/* Mobile Hamburger & Theme Toggle */}
        <div className="lg:hidden flex items-center gap-2">
          <button
            onClick={toggleTheme}
            className="p-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            aria-label="Toggle visual theme"
          >
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {isOpen && (
        <div className="lg:hidden bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-5 shadow-lg">
          <div className="flex flex-col space-y-1">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleNavClick(item)}
                className="text-left px-4 py-3 rounded-xl text-base font-medium text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800/60 hover:text-brand-600 transition-colors"
              >
                {item.name}
              </button>
            ))}
            <div className="pt-4 mt-2 border-t border-slate-100 dark:border-slate-800">
              <button
                onClick={handleGetInTouch}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-base font-semibold shadow-sm transition-colors"
              >
                Get in Touch
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
